import React, { useCallback, useState } from 'react';
import styles from './Devices.module.scss';

type SectionVariant = 'default' | 'warning' | 'success' | 'info';

const VARIANT_CLASS: Record<SectionVariant, string> = {
  default: '',
  warning: styles.sectionCardWarning,
  success: styles.sectionCardSuccess,
  info: styles.sectionCardInfo,
};

interface CollapsibleSectionProps {
  /** Section heading (also the persistence key, so keep it stable & unique). */
  title: string;
  /** Count rendered as "Title (count)". Omit to show the title alone. */
  count?: number;
  description?: React.ReactNode;
  /** Colored left accent — matches the existing sectionCard variants. */
  variant?: SectionVariant;
  /** Start collapsed on first visit (before any stored preference). */
  defaultCollapsed?: boolean;
  /** The table + pagination; hidden while collapsed. */
  children: React.ReactNode;
}

/** localStorage-backed collapsed flag so a section stays collapsed across visits. */
function usePersistentCollapse(key: string, defaultCollapsed: boolean): readonly [boolean, () => void] {
  const storageKey = `rescue:collapse:${key}`;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      const stored = window.localStorage.getItem(storageKey);
      return stored === null ? defaultCollapsed : stored === '1';
    } catch {
      // Private mode / disabled storage — fall back to the default.
      return defaultCollapsed;
    }
  });

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(storageKey, next ? '1' : '0');
      } catch {
        // Ignore storage failures — the in-memory state still updates.
      }
      return next;
    });
  }, [storageKey]);

  return [collapsed, toggle] as const;
}

/**
 * A section card whose body (table + pagination) collapses when the header is
 * clicked. Shared by every section on the Devices/Adoptions tabs so they behave
 * and look consistent; each table passes its own header text and its body as
 * children. Collapsed state persists per section (keyed by `title`).
 */
export default function CollapsibleSection({
  title,
  count,
  description,
  variant = 'default',
  defaultCollapsed = false,
  children,
}: CollapsibleSectionProps) {
  const [collapsed, toggle] = usePersistentCollapse(title, defaultCollapsed);
  const bodyId = `section-${title.replace(/\s+/g, '-').toLowerCase()}`;
  const cardClass = `${styles.sectionCard}${variant !== 'default' ? ` ${VARIANT_CLASS[variant]}` : ''}`;

  return (
    <div className={cardClass}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>
          <button
            type="button"
            className={styles.collapseToggle}
            aria-expanded={!collapsed}
            aria-controls={bodyId}
            onClick={toggle}
          >
            <svg
              className={`${styles.collapseChevron}${collapsed ? ` ${styles.collapseChevronCollapsed}` : ''}`}
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
            <span>
              {title}
              {count !== undefined ? ` (${count})` : ''}
            </span>
          </button>
        </h3>
        {description && <p className={styles.sectionDescription}>{description}</p>}
      </div>
      {!collapsed && <div id={bodyId}>{children}</div>}
    </div>
  );
}
